import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import PersonaBadge from '../common/PersonaBadge';
import { gymnasiumService } from '../../services/gymnasiumService';
import { EloRating } from '../../types/gymnasium';
import { FiRefreshCw, FiAward, FiTrendingUp, FiActivity } from 'react-icons/fi';
import GymSubHeader from './GymSubHeader';

const medalIcons = ['🥇', '🥈', '🥉'];

interface RankColumnProps {
  title: string;
  icon: React.ReactNode;
  ratings: EloRating[];
  valueKey: 'elo' | 'wins' | 'matches';
  valueLabel: string;
  accentColor: string;
  darkMode: boolean;
}

const RankColumn: React.FC<RankColumnProps> = ({ title, icon, ratings, valueKey, valueLabel, accentColor, darkMode }) => {
  const sorted = [...ratings].sort((a, b) => (b[valueKey] as number) - (a[valueKey] as number));

  return (
    <div className={`rounded-lg shadow-lg border-l-4 p-4 ${
      darkMode ? 'bg-gray-800 border-amber-600' : 'bg-white border-amber-500 shadow-sm'
    }`}>
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h2 className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{title}</h2>
      </div>
      {sorted.length === 0 ? (
        <p className={`text-xs text-center py-6 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No data yet</p>
      ) : (
        <div className="space-y-1">
          {sorted.map((r, i) => {
            const val = r[valueKey] as number;
            const valueColor = i === 0 ? 'text-amber-400' : i === 1 ? (darkMode ? 'text-gray-300' : 'text-gray-500') : i === 2 ? 'text-amber-700' : (darkMode ? 'text-gray-400' : 'text-gray-500');

            return (
              <div key={r.persona_id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${
                darkMode ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50'
              }`}>
                <span className="text-sm w-5 text-center shrink-0">
                  {medalIcons[i] || <span className={darkMode ? 'text-gray-600' : 'text-gray-400'}>{i + 1}</span>}
                </span>
                <div className="flex-1 min-w-0">
                  <PersonaBadge personaId={r.persona_id} personaName={r.name} size="md" clickable={false} />
                </div>
                <span className={`text-sm font-bold shrink-0 tabular-nums ${valueColor}`}>
                  {val}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Leaderboard: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [ratings, setRatings] = useState<EloRating[]>([]);
  const [matchCount, setMatchCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await gymnasiumService.getRankings();
      setRatings(data.ratings || []);
      setMatchCount(data.match_count || 0);
    } catch (err) {
      console.error('Failed to load rankings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalWins = ratings.reduce((sum, r) => sum + r.wins, 0);
  const totalMatches = ratings.reduce((sum, r) => sum + r.matches, 0);
  const avgElo = ratings.length > 0 ? Math.round(ratings.reduce((sum, r) => sum + r.elo, 0) / ratings.length) : 0;

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-5 ${
    darkMode ? 'bg-gray-800 border-amber-600' : 'bg-white border-amber-500 shadow-sm'
  }`;

  return (
    <div className="space-y-4">
      <GymSubHeader
        icon={<FiAward className="w-4 h-4" />}
        title="Rankings"
        subtitle={`${ratings.length} personas · ${matchCount} matches`}
        accentFrom="from-amber-400"
        accentTo="to-amber-500"
        actions={
          <button onClick={load} className={`p-1.5 rounded-lg ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        }
      />

      {/* Stat summary */}
      {ratings.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className={sectionClass}>
            <p className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Avg Elo</p>
            <p className={`text-xl font-bold ${darkMode ? 'text-amber-300' : 'text-amber-600'}`}>{avgElo}</p>
          </div>
          <div className={sectionClass}>
            <p className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Total Decisions</p>
            <p className={`text-xl font-bold ${darkMode ? 'text-green-300' : 'text-green-600'}`}>{totalWins}</p>
          </div>
          <div className={sectionClass}>
            <p className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Matches Played</p>
            <p className={`text-xl font-bold ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>{totalMatches}</p>
          </div>
        </div>
      )}

      {/* Empty state */}
      {ratings.length === 0 && !loading && (
        <div className={`${sectionClass} text-center py-12`}>
          <p className={`text-lg font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>No rankings yet</p>
          <p className={`text-sm mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Run some arena matches to build the leaderboard.</p>
          <button
            onClick={() => navigate('/gymnasium/arena')}
            className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 transition-all"
          >
            Start a Match
          </button>
        </div>
      )}

      {/* Three columns side by side */}
      {ratings.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <RankColumn
            title="By Rating"
            icon={<FiAward className="w-4 h-4 text-amber-400" />}
            ratings={ratings}
            valueKey="elo"
            valueLabel="Elo"
            accentColor="amber"
            darkMode={darkMode}
          />
          <RankColumn
            title="By Wins"
            icon={<FiTrendingUp className="w-4 h-4 text-green-400" />}
            ratings={ratings}
            valueKey="wins"
            valueLabel="W"
            accentColor="green"
            darkMode={darkMode}
          />
          <RankColumn
            title="By Matches"
            icon={<FiActivity className="w-4 h-4 text-blue-400" />}
            ratings={ratings}
            valueKey="matches"
            valueLabel="M"
            accentColor="blue"
            darkMode={darkMode}
          />
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
